/**
 * Login container — shadow de @plone-collective/volto-authomatic 2.0.4.
 *
 * Identico ao original, com dois desvios:
 * - fallback: quando o backend nao lista nenhum provedor externo (ex.: dev
 *   local com OIDC_ENABLED=false, que remove o plugin OIDC), renderiza o
 *   formulario de login classico do Volto em vez de deixar o usuario preso
 *   em "Escolha o provedor de autenticacao" vazio;
 * - AB#12105: com o login desligado neste frontend (RAZZLE_LOGIN_DESLIGADO,
 *   so no publico/www), /login mostra um aviso em vez do formulario e nao
 *   consulta os provedores. O bloqueio real esta na borda (403 na API).
 * @module components/Login/Login
 */
import React, { useEffect, useState } from 'react';
import {
  authomaticRedirect,
  listAuthOptions,
  oidcRedirect,
} from '@plone-collective/volto-authomatic/actions';
import { injectIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import VoltoLogin from '@plone/volto/components/theme/Login/Login';
import LoginForm from '@plone-collective/volto-authomatic/components/Login/LoginForm';
import qs from 'query-string';
import { useCookies } from 'react-cookie';
import { loginDesligado } from 'volto-ministerio-publico-federal/helpers/Auth/loginPublico';
import AvisoLoginDesligado from 'volto-ministerio-publico-federal/components/Auth/AvisoLoginDesligado';

/**
 * Get retur url function.
 * @function getReturnUrl
 * @param  {Object} location Location object.
 * @returns {string} Return url.
 */
function getReturnUrl(location) {
  return `${
    qs.parse(location.search).return_url ||
    (location.pathname === '/login'
      ? '/'
      : location.pathname.replace('/login', ''))
  }`;
}

/**
 * Login function.
 * @function Login
 * @returns {JSX.Element} Markup of the Login page.
 */
function Login({ intl, ...props }) {
  const dispatch = useDispatch();
  const [startedOAuth, setStartedOAuth] = useState(false);
  const [startedOIDC, setStartedOIDC] = useState(false);
  // Distingue "ainda nao buscou" de "buscou e nao ha provedores" (o reducer
  // authOptions inicia com options=[] e loading=false).
  const [requested, setRequested] = useState(false);
  const loading = useSelector((state) => state.authOptions.loading);
  const options = useSelector((state) => state.authOptions.options);
  const loginOAuthValues = useSelector((state) => state.authomaticRedirect);
  const loginOIDCValues = useSelector((state) => state.oidcRedirect);
  const location = useLocation();
  const [, setCookie] = useCookies();

  const semLogin = loginDesligado();

  useEffect(() => {
    if (semLogin) return;
    dispatch(listAuthOptions());
    setRequested(true);
  }, [dispatch, semLogin]);

  useEffect(() => {
    const next_url = loginOAuthValues.next_url;
    const session = loginOAuthValues.session;
    if (next_url && session && startedOAuth) {
      setStartedOAuth(false);
      // Give time to save state to localstorage
      setTimeout(function () {
        window.location.href = next_url;
      }, 500);
    }
  }, [startedOAuth, loginOAuthValues]);

  const onSelectProvider = (provider) => {
    setStartedOAuth(true);
    setCookie('return_url', getReturnUrl(location), { path: '/' });
    dispatch(authomaticRedirect(provider.id));
  };

  useEffect(() => {
    const next_url = loginOIDCValues.next_url;
    if (next_url && startedOIDC) {
      setStartedOIDC(false);
      setCookie('return_url', getReturnUrl(location), { path: '/' });
      // Give time to save state to localstorage
      setTimeout(function () {
        window.location.href = next_url;
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startedOIDC, loginOIDCValues]);

  useEffect(() => {
    if (
      options !== undefined &&
      options.length === 1 &&
      options[0].id === 'oidc'
    ) {
      setStartedOIDC(true);
      dispatch(oidcRedirect('oidc'));
    }
  }, [options, dispatch]);

  const resolved = requested && !loading;

  // Sem provedores externos: login classico do Volto (mesmo componente usado
  // pelo /fallback_login do addon).
  if (resolved && (options || []).length === 0) {
    return <VoltoLogin {...props} />;
  }

  if (semLogin) {
    return <AvisoLoginDesligado />;
  }

  return (
    <LoginForm
      loading={loading || !resolved}
      providers={options}
      action={'login'}
      onSelectProvider={onSelectProvider}
    />
  );
}

export default injectIntl(Login);
